import { Anchor, Button, Group, Loader, Select, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import {
  DataTable,
  FilterBar,
  PageHeader,
  RecordDrawer,
  StatusBadge,
  useSearchParamState,
} from "@how/ui";
import { semanticColor } from "@how/design-system";
import { isAuthorizationError } from "../../lib/query-client";
import {
  canRetry,
  STATUS_FILTERS,
  STATUS_LABEL,
  statusFilter,
  useProviders,
  useRetryWebhookEvent,
  useWebhookEvents,
  type WebhookEvent,
} from "./webhook-queries";

const PAGE_SIZE = 50;
const NO_SORTING: never[] = [];

const columns: ColumnDef<WebhookEvent>[] = [
  {
    accessorKey: "receivedAt",
    header: "Received",
    enableSorting: false,
    meta: { numeric: true },
    cell: (ctx) => new Date(ctx.row.original.receivedAt).toLocaleString(),
  },
  { accessorKey: "providerName", header: "Provider", enableSorting: false },
  {
    accessorKey: "eventType",
    header: "Event",
    enableSorting: false,
    cell: (ctx) => <span className="how-numeric">{ctx.row.original.eventType ?? "—"}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
    cell: (ctx) => (
      <StatusBadge
        domain="webhook_event_status"
        value={ctx.row.original.status}
        label={STATUS_LABEL[ctx.row.original.status]}
      />
    ),
  },
  {
    id: "church",
    header: "Church",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.church ?? (
        <Text size="sm" c="dimmed">
          No church
        </Text>
      ),
  },
  {
    accessorKey: "error",
    header: "Reason",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.error === null ? (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ) : (
        <Text size="sm" lineClamp={2} maw={420}>
          {ctx.row.original.error}
        </Text>
      ),
  },
];

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text c="dimmed" size="sm" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" ta="right" style={{ overflowWrap: "anywhere" }}>
        {children}
      </Text>
    </Group>
  );
}

/**
 * Retry a Stripe event that failed or gave up: HoW asks Stripe for the current state of what the event names and
 * applies it, within a minute (ADR 0060). Not destructive, so no confirmation; the button keeps focus and ignores
 * presses while it works, rather than disabling itself (docs/UI_STANDARDS.md).
 */
function RetryEvent({ event }: { readonly event: WebhookEvent }) {
  const retry = useRetryWebhookEvent();
  return (
    <Button
      variant="default"
      size="xs"
      leftSection={retry.isPending ? <Loader size={14} /> : undefined}
      aria-label={`Retry ${event.providerEventId}`}
      onClick={() => {
        if (retry.isPending) return;
        retry.mutate(event.id, {
          onSuccess: () => {
            notifications.show({
              color: semanticColor("info"),
              title: "Retrying",
              message: `${event.providerEventId} is tried again within a minute, from what Stripe says now.`,
            });
          },
          onError: () => {
            notifications.show({
              color: semanticColor("danger"),
              title: "Could not retry",
              message: "The event was not queued. Try again.",
            });
          },
        });
      }}
    >
      Retry
    </Button>
  );
}

/** One ledger row: what arrived, from whom, for which church, and how it ended. */
function WebhookEventDrawer({
  event,
  onClose,
}: {
  event: WebhookEvent | null;
  onClose: () => void;
}) {
  return (
    <RecordDrawer
      opened={event !== null}
      onClose={onClose}
      title={
        event === null ? "Webhook event" : `${event.providerName}: ${event.eventType ?? "event"}`
      }
      subtitle={
        event === null ? undefined : (
          <Text size="sm" c="dimmed" mb="md">
            {new Date(event.receivedAt).toLocaleString()}
          </Text>
        )
      }
    >
      {event === null ? null : (
        <Stack gap="lg">
          <Stack gap="xs">
            <Field label="Status">
              <StatusBadge
                domain="webhook_event_status"
                value={event.status}
                label={STATUS_LABEL[event.status]}
              />
            </Field>
            <Field label="Church">
              {event.church === null || event.tenantId === null ? (
                "No church matched"
              ) : (
                // Primary text with a standing underline, as on the Errors page: brand blue is 4.44:1 on the drawer.
                <Anchor
                  component={Link}
                  to={`/tenants/${event.tenantId}`}
                  size="sm"
                  c="var(--how-text-primary)"
                  underline="always"
                >
                  {event.church}
                </Anchor>
              )}
            </Field>
            <Field label="Provider">{event.providerName}</Field>
            <Field label="Provider's event id">{event.providerEventId}</Field>
            <Field label="Received">{new Date(event.receivedAt).toLocaleString()}</Field>
            <Field label="Handled">
              {event.processedAt === null
                ? "Not yet"
                : new Date(event.processedAt).toLocaleString()}
            </Field>
            <Field label="Attempts">{event.attempts}</Field>
            {event.status === "failed" && event.nextAttemptAt !== null ? (
              <Field label="Next attempt">{new Date(event.nextAttemptAt).toLocaleString()}</Field>
            ) : null}
          </Stack>
          {canRetry(event) ? (
            <Group justify="space-between" align="center" wrap="nowrap" gap="md">
              <Text size="sm" c="dimmed">
                {event.status === "dead"
                  ? "HoW gave up on this event. Retry it once the cause is fixed."
                  : "HoW tries this event again on its own. Retry now to skip the wait."}
              </Text>
              <RetryEvent event={event} />
            </Group>
          ) : null}
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Reason
            </Text>
            <Text size="sm" style={{ overflowWrap: "anywhere" }}>
              {event.error ?? "None recorded."}
            </Text>
          </Stack>
          <Text size="xs" c="dimmed">
            What the provider sent is never shown here: it can carry a payer&apos;s or a
            member&apos;s details. Look the event up in the provider&apos;s own dashboard by its id.
          </Text>
        </Stack>
      )}
    </RecordDrawer>
  );
}

/**
 * Every webhook from Stripe, Resend and Twilio lands in the ledger with how it ended (issue #256, architecture §13.4).
 * Gave up, failed and ignored come first: a failed Stripe event is tried again on its own; one HoW gave up on (after
 * ten attempts, or a short payment set aside on purpose) waits for a person, who can retry it here (ADR 0060). Since
 * #252 the Stripe webhook ignores an event naming another church's record, as a security signal. A platform super
 * admin's page: the database refuses everyone else, whatever this screen shows. Never the payload (D8).
 */
export function WebhookEventsPage() {
  const [search] = useSearchParamState("id");
  const [providerParam] = useSearchParamState("provider");
  const [statusParam] = useSearchParamState("status");
  const [page, setPage] = useSearchParamState("page", "1");
  const [selectedId, setSelectedId] = useSearchParamState("event");
  const [, setParams] = useSearchParams();
  /** A filter change and the return to the first page in one navigation (see ErrorsPage). */
  const setFilter = (key: "id" | "provider" | "status", value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };
  const providers = useProviders();

  const eventId = search.trim() === "" ? null : search.trim();
  const provider = providerParam === "" ? null : providerParam;
  const status = statusFilter(statusParam);
  const pageIndex = Math.max(0, Number.parseInt(page, 10) - 1 || 0);
  const events = useWebhookEvents({
    eventId,
    provider,
    status,
    pageIndex: eventId === null ? pageIndex : 0,
    pageSize: PAGE_SIZE,
  });
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: PAGE_SIZE }),
    [pageIndex],
  );
  const rows = events.data?.rows ?? [];
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const state = events.isPending
    ? "loading"
    : events.isError
      ? isAuthorizationError(events.error)
        ? "denied"
        : "error"
      : "ready";

  return (
    <>
      <PageHeader
        title="Webhook events"
        description="What Stripe, Resend and Twilio sent, and how each one ended. Events HoW gave up on, failed and ignored come first. A failed Stripe event is tried again on its own; one HoW gave up on can be retried here. An event naming another church's record, or a payment for less than was due, is set aside on purpose."
      />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setFilter("id", value);
        }}
        searchPlaceholder="Find an event id"
        filters={
          <>
            <Select
              aria-label="Filter by status"
              disabled={eventId !== null}
              data={Object.entries(STATUS_FILTERS).map(([value, f]) => ({
                value,
                label: f.label,
              }))}
              value={status}
              allowDeselect={false}
              onChange={(value) => {
                setFilter("status", value === "attention" ? null : value);
              }}
              w={{ base: "100%", sm: 240 }}
            />
            <Select
              aria-label="Filter by provider"
              placeholder="Any provider"
              clearable
              disabled={eventId !== null}
              data={(providers.data ?? []).map((p) => ({ value: p.key, label: p.name }))}
              value={provider}
              onChange={(value) => {
                setFilter("provider", value);
              }}
              w={{ base: "100%", sm: 200 }}
            />
          </>
        }
      />
      <DataTable
        label="Webhook events"
        density="dense"
        minWidth={960}
        columns={columns}
        rows={rows}
        totalRows={events.data?.total ?? 0}
        status={state}
        pagination={pagination}
        onPaginationChange={(updater) => {
          const next = typeof updater === "function" ? updater(pagination) : updater;
          setPage(String(next.pageIndex + 1));
        }}
        sorting={NO_SORTING}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setSelectedId(row.id);
        }}
        emptyTitle={
          eventId !== null
            ? `No event with the id ${eventId}`
            : status === "attention"
              ? "Nothing gave up, failed or was ignored"
              : "No events"
        }
        emptyDescription={
          eventId !== null
            ? "Check the id in the provider's dashboard. Resend and Twilio events are recorded here once their webhooks write the ledger (#276)."
            : status === "attention" && provider === null
              ? "Every webhook that arrived was handled."
              : "Try another status or provider."
        }
        errorMessage="Could not load webhook events."
        onRetry={() => {
          void events.refetch();
        }}
      />
      <WebhookEventDrawer
        event={selected}
        onClose={() => {
          setSelectedId(undefined);
        }}
      />
    </>
  );
}
